import { MonetizationType, OfferHistory } from '@/@types/graphql-types';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/helpers/composables/useStores';

export type SlimOfferHistory = Omit<OfferHistory, 'package'> & {
	package: Partial<OfferHistory['package']>;
};

export const useStreamingOffersHistory = ({
	offersHistory,
	titleName,
}: {
	offersHistory: MaybeRefOrGetter<SlimOfferHistory[]>;
	titleName?: MaybeRefOrGetter<string>;
}) => {
	const { country, language } = useLanguageStore();

	const { t, tc } = useI18n();

	const dateFormatter = new Intl.DateTimeFormat(`${toValue(language)}-${toValue(country)}`, {
		month: 'short',
		year: 'numeric',
	});

	const spinningText = computed(() => {
		const title = toValue(titleName);
		const countryName = tc(`WEBAPP_COUNTRY_${toValue(country)}`);
		const total = toValue(offersHistory).length;
		const itemsWithLatestEnd = (() => {
			const itemsWithLatestEndDate = toValue(offersHistory).map(item => ({
				item,
				latestEnd: new Date(Math.max(...item.dateRanges.map(range => new Date(range.end).getTime()))),
			}));

			const maxLatestEnd = itemsWithLatestEndDate.reduce(
				(max, curr) => (curr.latestEnd > max ? curr.latestEnd : max),
				itemsWithLatestEndDate[0]?.latestEnd ?? new Date(0)
			);

			return itemsWithLatestEndDate
				.filter(
					({ latestEnd }) =>
						latestEnd.getFullYear() === maxLatestEnd.getFullYear() &&
						latestEnd.getMonth() === maxLatestEnd.getMonth()
				)
				.map(({ item }) => item);
		})();
		const latestItem = toValue(offersHistory)
			.map(item => ({
				item,
				latestEnd: new Date(Math.max(...item.dateRanges.map(range => new Date(range.end).getTime()))),
			}))
			.sort((a, b) => b.latestEnd.getTime() - a.latestEnd.getTime())[0]?.item;
		const lastService = (() => {
			const serviceNames = itemsWithLatestEnd.map(item => item.package.clearName);
			if (serviceNames.length === 1) {
				return serviceNames[0] ?? '';
			} else if (serviceNames.length === 2) {
				return `${serviceNames[0]} and ${serviceNames[1]}`;
			} else if (serviceNames.length > 2) {
				return `${serviceNames.slice(0, -1).join(', ')} and ${serviceNames[serviceNames.length - 1]}`;
			}
			return '';
		})();
		const lastDate = new Intl.DateTimeFormat(`${toValue(language)}-${toValue(country)}`, {
			month: 'long',
			year: 'numeric',
		}).format(
			new Date(
				latestItem.dateRanges.sort((a, b) => new Date(b.end).getTime() - new Date(a.end).getTime())[0]?.end
			)
		);
		const latestServices = toValue(offersHistory)
			.map(item => ({
				item,
				latestEnd: new Date(Math.max(...item.dateRanges.map(range => new Date(range.end).getTime()))),
			}))
			.sort((a, b) => b.latestEnd.getTime() - a.latestEnd.getTime())
			.slice(0, 3)
			.map(entry => entry.item);
		const serviceNames = latestServices.map(item => item.package.clearName);
		let serviceListString = '';
		if (serviceNames.length === 1) {
			serviceListString = serviceNames[0] ?? '';
		} else if (serviceNames.length === 2) {
			serviceListString = `${serviceNames[0]} and ${serviceNames[1]}`;
		} else if (serviceNames.length > 2) {
			serviceListString = `${serviceNames.slice(0, -1).join(', ')} and ${serviceNames[serviceNames.length - 1]}`;
		}

		if (toValue(offersHistory).length === 1) {
			return t('WEBAPP_STREAMING_OFFER_HISTORY_SPINNING_TEXT_1_PREV_OFFER', {
				title,
				country: countryName,
				service: lastService,
				date: lastDate,
			});
		} else {
			return t('WEBAPP_STREAMING_OFFER_HISTORY_SPINNING_TEXT_MULTIPLE_PREV_OFFERS', {
				title,
				country: countryName,
				total,
				servicelist: serviceListString,
				service: lastService,
				date: lastDate,
			});
		}
	});

	const sortedOffersHistory = computed(() => {
		return [...toValue(offersHistory)]
			.sort((b, a) =>
				a.dateRanges[a.dateRanges?.length - 1].end.localeCompare(b.dateRanges[b.dateRanges?.length - 1].end)
			)
			.filter(item => !item.package.monetizationTypes?.includes(MonetizationType.Cinema));
	});

	return {
		dateFormatter,
		spinningText,
		sortedOffersHistory,
	};
};
